import api from "@/helpers/api";
import { getChunks } from "@/helpers/utils";

const state = {
  productChunks: null,
  filteredProductChunks: null,
  selectedProduct: null,
  totalProducts: 0,
  isFetching: false,
};

const getters = {
  filteredProductChunks: (state) => state.filteredProductChunks,
  productChunks: (state) => state.productChunks,
  selectedProduct: (state) => state.selectedProduct,
  isFetching: (state) => state.isFetching,
};

const actions = {
  /***************
   * @Action : fetchProducts
   * @Desc :
   *    - fetches product IDs, divides the IDs into chunks
   *    - then fetches the details of the first few chunks.
   *    - Also used as a helper function for fetching products within a category
   ***************/
  fetchProducts: async (state, payload = {}) => {
    const { filters, chunkIndexes, fromSearch } = payload;

    try {
      const data = await api.post(
        `/products${filters ? "/filter" : ""}`,
        filters ? { ...filters } : {}
      );

      const { chunks } = await state.dispatch("fetchProductChunks", {
        ids: data.productIds,
        chunkIndexes: chunkIndexes || [0, 1, 2, 3, 4],
      });

      const productListObj = {
        chunks,
        total: +data.total,
      };

      if (!filters?.categoryIdentifier && !fromSearch) {
        state.commit("setProducts", { productList: productListObj });
      }

      return productListObj;
    } catch (err) {
      console.log(err);
      throw err;
    }
  },

  /***************
   * @Action : fetchProductChunks
   * @Desc : Divides product IDs into chunks, then retreives products from specified chunk indexes
   *    used as a helper function for 'fetchProducts'
   ***************/
  fetchProductChunks: async (state, payload) => {
    state.commit("setIsFetching", true);
    const { ids = [], chunkIndexes = [] } = payload;

    const productIDs = [];
    const chunkIDs = getChunks(ids, 12) || {};
    const totalChunks = Object.keys(chunkIDs).length;

    if (chunkIndexes.length) {
      chunkIndexes.forEach((chunkIndex) => {
        if (chunkIndex < totalChunks) {
          productIDs.push(...chunkIDs[chunkIndex].ids);
          chunkIDs[chunkIndex].loaded = true;
        }
      });
    } else {
      productIDs.push(...ids);
    }

    if (productIDs.length > 100) {
      state.commit("setIsFetching", false);
      throw new Error("Total products must be less than or equal to 100");
    }

    try {
      const { products } = await api.post("/products", productIDs);

      if (totalChunks === 1 && !chunkIndexes.length) {
        return {
          products,
        };
      }

      for (const chunk in chunkIDs) {
        if (chunkIDs[chunk].loaded) {
          chunkIDs[chunk].products = chunkIDs[chunk].ids.map((id) => {
            return products.find((product) => product.productId === id);
          });
        }
      }

      state.commit("setIsFetching", false);
      return { chunks: chunkIDs };
    } catch (err) {
      state.commit("setIsFetching", false);
      console.log(err);
      throw err;
    }
  },

  /***************
   * @Action : filterProducts
   * @Desc : filters products based on a search string,
   ***************/
  filterProducts: async (state, { searchString, language, chunkIndexes }) => {
    if (!searchString?.length) {
      state.commit("clearFilteredProducts");
      if (!state.productChunks) {
        await state.dispatch("fetchProducts");
      }
      return;
    }

    const params = {
      fromSearch: true,
      filters: {
        descriptionShort: {
          [language]: searchString,
        },
      },
    };

    try {
      const productList = await state.dispatch("fetchProducts", {
        ...params,
        ...(chunkIndexes?.length ? { chunkIndexes } : {}),
      });

      state.commit("setProducts", {
        productList: {
          chunks: productList.chunks,
          total: productList.total,
        },
        fromSearch: true,
      });
    } catch (err) {
      console.log(err);
      throw err;
    }
  },

  /***************
   * @Action : fetchSingleProduct
   * @Desc : Fetch a single product based on the ID provided,
   ***************/
  fetchSingleProduct: async (state, { id, isSelected }) => {
    try {
      const data = await api.post("/products", [id]);

      if (isSelected) {
        state.commit("setProduct", { product: data.products[0] });
      }

      return data.products[0];
    } catch (err) {
      console.log(err);
      throw err;
    }
  },
};

const mutations = {
  setProducts: (state, payload) => {
    const { productList, fromSearch, chunkIndex } = payload;

    const productKey = !fromSearch ? "productChunks" : "filteredProductChunks";
    const filteredProducts = state.filteredProductChunks;

    if (!fromSearch && filteredProducts) {
      state.filteredProductChunks = null;
    }

    if (productList.total) {
      state.totalProducts = productList.total;
    }

    if (!productList.chunks) {
      state[productKey][chunkIndex].products = productList.products || [];
      state[productKey][chunkIndex].loaded = true;
      return;
    }

    for (let key in productList.chunks) {
      const isSameIDs =
        JSON.stringify(state[productKey]?.[key]?.ids) ===
        JSON.stringify(productList.chunks[key]?.ids);

      if (state[productKey]?.[key]?.loaded && isSameIDs) {
        productList.chunks[key] = state[productKey][key];
      }
    }

    state[productKey] = productList.chunks;
  },
  setSelectedProduct: (state, payload) => {
    state.selectedProduct = payload;
  },
  setIsFetching: (state, isFetching) => {
    state.isFetching = isFetching;
  },
  clearSelectedProduct: (state) => {
    state.selectedProduct = null;
  },
  clearFilteredProducts: (state) => {
    state.filteredProductChunks = null;
  },
  clearProducts: (state) => {
    state.totalProducts = 0;
    state.productChunks = {};
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
